import { Stack } from "@mui/material";
import { ReactNode } from "react";

import { cn } from "../utils/styling";
import { Icon } from "./Icon";

export const ICON_SIZE_CLASS = "w-10 h-10";

type StepIconAndLabelProps = {
  label: string | undefined;
  layout?: StepIconLayout;
  onClick?: () => void;
  status: Status;
  stepNumber: number;
};

export const StepIconAndLabel = ({ label, layout = "stacked", onClick, status, stepNumber }: StepIconAndLabelProps) => {
  const config = getStepIconConfig(status, layout, stepNumber, onClick);
  const badge = (
    <Stack className={cn("justify-center rounded-full", config.sizeClass, config.badgeClass)} onClick={onClick}>
      <span className="font-ds-body-semibold-md">{config.iconText}</span>
    </Stack>
  );
  if (layout === "inline") {
    return (
      <Stack className="gap-2">
        {badge}
        <span className={config.labelClass}>{label}</span>
      </Stack>
    );
  }
  return (
    <Stack className="w-32 flex-col gap-2">
      {badge}
      <div className="relative w-full">
        <div
          className={cn(
            "absolute left-1/2 w-42 -translate-x-1/2 text-center font-ds-body-semibold-sm",
            config.labelClass
          )}
        >
          {label}
        </div>
      </div>
    </Stack>
  );
};

export const InlineStepIconAndLabel = (props: Omit<StepIconAndLabelProps, "layout">) => {
  return <StepIconAndLabel {...props} layout="inline" />;
};

const DONE_ICON = <Icon className="w-8" color="white" iconName="Checkmark" size="lg" />;

const STACKED_CONFIGS = (
  stepNumberDisplay: string,
  onClick?: () => void
): Record<Status, Omit<StepIconConfig, "sizeClass">> => ({
  active: {
    badgeClass: "text-white bg-core-secondary-blue-500",
    iconText: stepNumberDisplay,
  },
  disabled: {
    badgeClass: "bg-lds-element-disabled text-lds-foreground-disabled",
    iconText: stepNumberDisplay,
    labelClass: "text-lds-foreground-disabled",
  },
  done: {
    badgeClass: cn("bg-core-plunge-pool-800 text-white", onClick && "cursor-pointer"),
    iconText: DONE_ICON,
  },
});

const LABEL_CLASS = "text-lds-foreground-primary font-ds-body-bold-md";

const INLINE_CONFIGS = (
  stepNumberDisplay: string,
  onClick?: () => void
): Record<Status, Omit<StepIconConfig, "sizeClass">> => ({
  active: {
    ...STACKED_CONFIGS(stepNumberDisplay, onClick).active,
    labelClass: LABEL_CLASS,
  },
  disabled: {
    ...STACKED_CONFIGS(stepNumberDisplay, onClick).disabled,
    badgeClass: "bg-lds-surface-subtle text-lds-foreground-disabled",
    labelClass: LABEL_CLASS,
  },
  done: {
    ...STACKED_CONFIGS(stepNumberDisplay, onClick).done,
    labelClass: LABEL_CLASS,
  },
});

export type Status = "active" | "disabled" | "done";

type StepIconConfig = {
  badgeClass: string;
  iconText: ReactNode;
  labelClass?: string;
  sizeClass: string;
};

type StepIconLayout = "inline" | "stacked";

function getStepIconConfig(
  status: Status,
  layout: StepIconLayout,
  stepNumber: number,
  onClick?: () => void
): StepIconConfig {
  const stepNumberDisplay = layout === "stacked" ? `${stepNumber}`.padStart(2, "0") : `${stepNumber}`;
  const sizeClass = layout === "stacked" ? "w-10 h-10" : "h-8 w-8";
  const configs =
    layout === "stacked" ? STACKED_CONFIGS(stepNumberDisplay, onClick) : INLINE_CONFIGS(stepNumberDisplay, onClick);
  return { ...configs[status], sizeClass };
}
